import api from 'lib/trpcClient';
import { useRef } from 'react';
import useCurrentUser from '../currentUser/useCurrentUser';
import { keepPreviousData } from '@tanstack/react-query';
import { BackgroundExportStatus } from 'further-types/background-exports';
import { QUERY_STALE_TIME_MS } from 'further-ui/config';
import { downloadLinkAsFile } from 'further-ui/utils';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

export const useBackgroundExports = () => {
  const countRef = useRef(0);
  const apiUtils = api.useUtils();
  const user = useCurrentUser();
  const [canCreateExports] = useHasPermissions([
    RolePermission.CreateBackgroundExport,
  ]);

  const { data: backgroundExports } =
    api.backgroundExports.getBackgroundExports.useQuery(undefined, {
      placeholderData: keepPreviousData,
      enabled: !!user.profile.data && canCreateExports,
      refetchInterval: (query) => {
        const hasPendingExports = query.state.data?.results?.some(
          (backgroundExport) =>
            backgroundExport.status === BackgroundExportStatus.Pending,
        );

        if (!hasPendingExports) {
          countRef.current = 0;
          return false;
        }

        const elapsedTime = countRef.current * 5;
        countRef.current++;

        if (elapsedTime <= 240) {
          return 5000;
        }

        if (elapsedTime <= 1440) {
          return 15000;
        }

        countRef.current = 0;
        return false;
      },
      refetchOnWindowFocus: false,
      staleTime: QUERY_STALE_TIME_MS,
    });

  const downloadBackgroundExport =
    api.backgroundExports.downloadBackgroundExport.useMutation({
      onMutate: () => {
        countRef.current = 0; // Reset long poll
      },
      onSuccess: (data) => {
        downloadLinkAsFile(data.downloadLink, data.downloadName);
      },
    });

  const createBackgroundExport =
    api.backgroundExports.createBackgroundExport.useMutation({
      onMutate: () => {
        countRef.current = 0; // Reset long poll
      },
      onSuccess: () => {
        apiUtils.backgroundExports.getBackgroundExports.invalidate();
      },
    });

  const deleteBackgroundExport =
    api.backgroundExports.deleteBackgroundExport.useMutation({
      onSuccess: () => {
        apiUtils.backgroundExports.getBackgroundExports.invalidate();
      },
    });

  const retryBackgroundExport =
    api.backgroundExports.retryBackgroundExport.useMutation({
      onMutate: async ({ exportId }) => {
        await apiUtils.backgroundExports.getBackgroundExports.cancel();

        const previous =
          apiUtils.backgroundExports.getBackgroundExports.getData();

        apiUtils.backgroundExports.getBackgroundExports.setData(
          undefined,
          (old) => {
            if (!old) {
              return old;
            }

            // optimistically set export status to pending
            return {
              ...old,
              results: old.results.map((e) =>
                e._id === exportId
                  ? { ...e, status: BackgroundExportStatus.Pending }
                  : e,
              ),
            };
          },
        );
        return { previous };
      },
      onError: (_err, _vars, context) => {
        if (context?.previous) {
          apiUtils.backgroundExports.getBackgroundExports.setData(
            undefined,
            context.previous,
          );
        }
      },
      onSuccess: () => {
        countRef.current = 0;
        apiUtils.backgroundExports.getBackgroundExports.invalidate();
      },
    });

  const acknowledgeBackgroundExports =
    api.backgroundExports.acknowledgeBackgroundExports.useMutation({
      onSuccess: () => {
        apiUtils.backgroundExports.getBackgroundExports.invalidate();
      },
    });

  return {
    backgroundExports,
    downloadBackgroundExport,
    createBackgroundExport,
    deleteBackgroundExport,
    retryBackgroundExport,
    acknowledgeBackgroundExports,
    isExporting: createBackgroundExport.isPending,
  };
};
